/**
 * ErrorBoundary Component
 *
 * A React error boundary that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI instead of
 * crashing the whole application.
 *
 * Requirements: 2.8, 3.5, 11.4, 11.5
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  ErrorCircle24Regular,
  ArrowSync20Regular,
  Home20Regular,
} from '@fluentui/react-icons';
import styles from './ErrorBoundary.module.css';

// ============================================================================
// Types
// ============================================================================

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional fallback UI to render when an error occurs */
  fallback?: ReactNode;
  /** Optional callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Optional custom error title */
  errorTitle?: string;
  /** Optional custom error message */
  errorMessage?: string;
  /** Whether to show error details in development mode */
  showDetails?: boolean;
  /** Optional callback for retry action */
  onRetry?: () => void;
  /** Optional callback for navigation to home */
  onNavigateHome?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ErrorBoundary catches JavaScript errors in child components and displays
 * a user-friendly error message with options to retry or navigate away.
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom error handling
 * <ErrorBoundary
 *   onError={(error) => logError(error)}
 *   onRetry={() => window.location.reload()}
 *   errorTitle="Something went wrong"
 *   errorMessage="We're sorry, but something unexpected happened."
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset the error state and attempt to re-render children
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  /**
   * Navigate to home page
   */
  handleNavigateHome = (): void => {
    if (this.props.onNavigateHome) {
      this.props.onNavigateHome();
    } else {
      // Default: navigate to evidence extractor home
      window.location.href = '/evidence-extractor';
    }
  };

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const {
      children,
      fallback,
      errorTitle = 'Something went wrong',
      errorMessage = 'We encountered an unexpected error. Please try again or contact support if the problem persists.',
      showDetails = import.meta.env.DEV,
    } = this.props;

    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className={styles.errorBoundary} role="alert" aria-live="assertive">
          <div className={styles.errorContent}>
            {/* Error Icon */}
            <div className={styles.iconContainer}>
              <ErrorCircle24Regular className={styles.errorIcon} />
            </div>

            {/* Error Title */}
            <h1 className={styles.errorTitle}>{errorTitle}</h1>

            {/* Error Message */}
            <p className={styles.errorMessage}>{errorMessage}</p>

            {/* Error Details (Development Mode) */}
            {showDetails && error && (
              <details className={styles.errorDetails}>
                <summary className={styles.detailsSummary}>
                  Error Details (Development Mode)
                </summary>
                <div className={styles.detailsContent}>
                  <div className={styles.errorName}>
                    <strong>Error:</strong> {error.name}
                  </div>
                  <div className={styles.errorStack}>
                    <strong>Message:</strong> {error.message}
                  </div>
                  {error.stack && (
                    <pre className={styles.stackTrace}>{error.stack}</pre>
                  )}
                  {errorInfo?.componentStack && (
                    <>
                      <div className={styles.componentStackLabel}>
                        <strong>Component Stack:</strong>
                      </div>
                      <pre className={styles.componentStack}>
                        {errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={this.handleRetry}
                aria-label="Try again"
              >
                <ArrowSync20Regular className={styles.buttonIcon} />
                <span>Try Again</span>
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={this.handleReload}
                aria-label="Reload page"
              >
                <span>Reload Page</span>
              </button>
              <button
                type="button"
                className={styles.tertiaryButton}
                onClick={this.handleNavigateHome}
                aria-label="Go to home"
              >
                <Home20Regular className={styles.buttonIcon} />
                <span>Go Home</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
